
import { Component, Prop, Vue } from "vue-property-decorator";
import PModal from "@/components/look/PModal.vue";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import Multiselect from "vue-multiselect";
import { Getter } from "vuex-class";
import {
  IDeclaredValuePack,
  ILimitExceeded,
  ISolCustomerCurrency,
  ISolCustomerPack,
  IUserClientSubclientServicepointData,
  IUserDetailData,
  IValuePack,
  SolCustomerOrderType,
} from "@/entities";
import DeclaredValuePackDetails from "@/components/orders/agile_pickup/DeclaredValuePackDetails.vue";
import { ISaveDeclaredValuePackRequestBody } from "@/services";

@Component({
  name: "add-edit-declared-value-pack-modal",
  components: {
    DeclaredValuePackDetails,
    Multiselect,
    PModal,
    ValidationObserver,
    ValidationProvider,
  },
})
export default class AddEditDeclaredValuePackModal extends Vue {
  @Prop({ required: true }) packs!: ISolCustomerPack[];
  @Prop({ required: true, default: "", type: String }) serviceCode!: string;
  @Prop({ required: false, default: null }) declaredValue!: IDeclaredValuePack;
  @Prop({ required: false, default: null })
  servicepointSelected!: IUserClientSubclientServicepointData | null;

  @Getter("getLoggedUser") getLoggedUser!: IUserDetailData;
  @Getter("getSolCustomerCurrencies") currencies!: ISolCustomerCurrency[];

  public packSelected: ISolCustomerPack | null = null;
  public quantity: number | null = null;
  public declaredValues: IValuePack[] = [];
  $refs!: {
    formValuesPack: InstanceType<typeof ValidationObserver>;
  };
  private valueEdit: IValuePack | null = null;

  initData(): void {
    this.resetForm();
    this.declaredValues = [];
    this.declaredValues.push(...(this.declaredValue?.vl?.map((value) => ({ ...value })) || []));
  }

  resetForm(): void {
    this.valueEdit = null;

    this.packSelected = null;
    this.quantity = null;
    this.$refs.formValuesPack?.reset();
  }

  onEdit(value: IValuePack): void {
    this.packSelected = this.packs.find((p) => p.code === value.pc) ?? null;
    this.quantity = value.pq!;

    this.valueEdit = value;
  }

  onSubmit(): void {
    if (this.valueEdit) {
      this.valueEdit.cic = this.packSelected?.denominations?.[0]?.currencyIsoCode;
      this.valueEdit.pc = this.packSelected?.code;
      this.valueEdit.pq = Number(this.quantity);
    } else {
      const declaredValue = this.declaredValues.find((dv) => dv.pc === this.packSelected?.code);

      if (declaredValue) {
        declaredValue.pq = Number(declaredValue.pq) + Number(this.quantity);
      } else {
        this.declaredValues.push({
          cic: this.packSelected?.denominations?.[0]?.currencyIsoCode,
          pc: this.packSelected?.code,
          pq: Number(this.quantity),
        });
      }
    }

    this.resetForm();
  }

  hideModal(): void {
    this.$bvModal.hide("addEditDeclaredValuePackModal");
  }

  async onSave(): Promise<void> {
    const request: ISaveDeclaredValuePackRequestBody = {
      ct: this.getLoggedUser.country!.code!,
      svc: this.serviceCode,
      dr: [
        {
          co: this.declaredValue?.co,
          vl: this.declaredValues || [],
        },
      ],
    };

    const limitExceeded = await this.isLimitExceeded(request.dr);
    if (limitExceeded.length > 0) {
      const items = limitExceeded
        .map(
          (l) =>
            `<li>
                ${this.$options.filters?.currency(l.limitValue, this.getCurrencySymbol(l.currency))}
            </li>`
        )
        .join("");
      this.$swal({
        title: this.$t("message.newOrder.limit.exceeded.title").toString(),
        html: `${this.$t("message.newOrder.limits.exceeded.description")} 
          <ul class="swal-limits-list">
            ${items}
          </ul>`,
        icon: "warning",
        confirmButtonText: this.$t("label.ok").toString(),
      });
      return;
    }

    let success: boolean;
    if (this.declaredValue) {
      success = await this.$services.solServiceApi.updateDeclaredValuePack(request);
    } else {
      success = await this.$services.solServiceApi.saveDeclaredValuePack(request);
    }

    if (success) {
      this.$emit("success");
      this.hideModal();
    }
  }

  async isLimitExceeded(dr: IDeclaredValuePack[]): Promise<ILimitExceeded[]> {
    const servicepointLimits = await this.$services.servicepoint.getServicepointLimits(
      this.servicepointSelected!.id!,
      SolCustomerOrderType.PICKUP
    );
    const amountsByCurrency = this.getAmountPacksByCurrency(dr);

    let limitExceeded: ILimitExceeded[] = [];

    amountsByCurrency.forEach((value) => {
      const limit = servicepointLimits?.limits.find((l) => l.isoCode === value.currency)?.amount;

      if (limit && value.import > limit) {
        limitExceeded.push({ currency: value.currency, import: value.import, limitValue: limit });
      }
    });
    return limitExceeded;
  }

  getAmountPacksByCurrency(dr: IDeclaredValuePack[]): ILimitExceeded[] {
    const values = dr.flatMap((d) => d.vl || []);
    const result: ILimitExceeded[] = [];

    values.forEach((valuePack) => {
      const pack = this.packs.find((p) => p.code === valuePack.pc);
      if (!pack) return;

      const pq = valuePack.pq ?? 0;

      pack.denominations?.forEach((denomination) => {
        const isoCode = denomination.currencyIsoCode ?? "";
        const denominationTotal = (denomination.quantity ?? 0) * (denomination.value ?? 0);
        const totalAmount = denominationTotal * pq;

        const existing = result.find((item) => item.currency === isoCode);
        if (existing) {
          existing.import += totalAmount;
        } else {
          result.push({ currency: isoCode, import: totalAmount });
        }
      });
    });
    return result;
  }

  getCurrencySymbol(isoCode: string): string {
    const currency = this.currencies.find((c) => c.isoCode === isoCode);
    return currency?.symbol || isoCode;
  }
}
